// Nights and the rota: whose turn it is, and the overnight toilet trip.
//
// Small puppies can't hold on all night. The usual UK advice (Dogs Trust,
// RSPCA): a last wee at bedtime, one quiet trip in the small hours — carry
// him out, no play, straight back to bed — then push the alarm later every
// few dry nights until he sleeps through, usually by 12–16 weeks.

import { DAY, HOUR, atClock, clockToMinutes, dayStart, formatTime, inClockWindow, minutesOfDay } from './time.ts';
import type { Household, LogEvent, Millis, Person, RotaSlot } from './types.ts';

export const ROTA_SLOTS: { slot: RotaSlot; label: string; detail: string }[] = [
  { slot: 'morning', label: 'Morning', detail: 'Up with him first thing — straight out for a wee.' },
  { slot: 'day', label: 'Daytime', detail: 'At home with him. Toilet alerts go to them when nobody’s presence is known.' },
  { slot: 'night', label: 'Night trip', detail: 'Does the overnight toilet trip (the night that starts that evening).' },
];

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Which weekday a slot belongs to at `now`: a night (or small hours) belongs to the evening it started. */
export function rotaDay(slot: RotaSlot, now: Millis): number {
  const d = new Date(now);
  // Before midday, "tonight" is still the night that started yesterday evening.
  if (slot === 'night' && d.getHours() < 12) return (d.getDay() + 6) % 7;
  return d.getDay();
}

/** The calendar date a slot belongs to at `now` (a night belongs to its evening). */
export function rotaDate(slot: RotaSlot, now: Millis): string {
  const d = new Date(now);
  if (slot === 'night' && d.getHours() < 12) d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function onDuty(household: Household | undefined, slot: RotaSlot, now: Millis): Person | undefined {
  const id = household?.rotaSwaps?.[`${rotaDate(slot, now)}:${slot}`] ?? household?.rota?.[slot]?.[rotaDay(slot, now)];
  return id ? household?.people.find((p) => p.id === id) : undefined;
}

/** The moment that means "tonight" at `now`: now if the night is under way, else this evening. */
export function tonightAt(household: Pick<Household, 'nightStart' | 'nightEnd'>, now: Millis): Millis {
  return inClockWindow(now, household.nightStart, household.nightEnd) ? now : atClock(now, '18:00');
}

/** A one-off swap for one slot on one day; swaps older than two weeks are tidied away. */
export function withSwap(household: Household, slot: RotaSlot, at: Millis, personId: string): Record<string, string> {
  const cutoff = rotaDate('day', at - 14 * DAY);
  const kept = Object.fromEntries(Object.entries(household.rotaSwaps ?? {}).filter(([k]) => k.slice(0, 10) >= cutoff));
  return { ...kept, [`${rotaDate(slot, at)}:${slot}`]: personId };
}

/** Suggested alarm for his age, or undefined once most pups sleep through. */
export function suggestedNightTrip(weeks: number): string | undefined {
  if (weeks < 10) return '02:30';
  if (weeks < 12) return '03:30';
  if (weeks < 15) return '04:30';
  return undefined;
}

const addMins = (hhmm: string, mins: number) => {
  const t = (clockToMinutes(hhmm) + mins + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

export interface NightNight {
  /** When the night started (nightStart that evening). */
  start: Millis;
  /** It's over (nightEnd has passed). */
  finished: boolean;
  logged: boolean;
  accident?: LogEvent;
}

/** The last `n` nights, newest first (the one under way, if any, first): was anything logged, and was there an accident indoors? */
export function recentNights(events: LogEvent[], household: Pick<Household, 'nightStart' | 'nightEnd'>, now: Millis, n = 7): NightNight[] {
  const out: NightNight[] = [];
  // The evening of the most recent night that has started.
  let evening = atClock(now, household.nightStart);
  if (now < evening) evening = atClock(dayStart(now, -1), household.nightStart);
  const wraps = clockToMinutes(household.nightEnd) < clockToMinutes(household.nightStart);
  for (let i = 0; i < n; i++) {
    // Day by day on the calendar, so a clock change can't skip or repeat a night.
    const start = atClock(dayStart(evening, -i), household.nightStart);
    const end = atClock(dayStart(evening, wraps ? 1 - i : -i), household.nightEnd);
    const inNight = events.filter((e) => e.at >= start && e.at < Math.min(end, now));
    const around = events.some((e) => e.at >= start - 3 * HOUR && e.at < end + 3 * HOUR);
    const accident = inNight.find((e) => (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside');
    out.push({ start, finished: end <= now, logged: around, accident });
  }
  return out;
}

export interface NightPlan {
  /** Alarm time tonight, if a night trip is still part of the routine. */
  time?: string;
  /** The suggestion for his age (shown when no time has been set). */
  suggested?: string;
  who?: Person;
  /** Consecutive dry nights (with logging) up to last night. */
  dryNights: number;
  lastAccident?: LogEvent;
  /** One practical next step, e.g. "3 dry nights — try 03:00 tonight." */
  advice: string;
  /** Proposed new alarm time to go with the advice. */
  proposeTime?: string;
  /** Suggest dropping the night trip altogether. */
  proposeOff?: boolean;
}

export function nightTripPlan({ household, events, weeks, now }: { household: Household; events: LogEvent[]; weeks: number; now: Millis }): NightPlan {
  const suggested = suggestedNightTrip(weeks);
  const off = household.nightTrip?.off;
  const time = off ? undefined : (household.nightTrip?.time ?? suggested);
  // In the day, "tonight" is the coming night — not the one that ended this morning.
  const who = onDuty(household, 'night', tonightAt(household, now));
  const all = recentNights(events, household, now, 8);
  // Only finished nights count as dry; the one under way counts only if it already had an accident.
  const underway = all[0] && !all[0].finished ? all[0] : undefined;
  const nights = all.filter((n) => n.finished).slice(0, 7);
  let dryNights = 0;
  for (const n of nights) {
    if (!n.logged || n.accident) break;
    dryNights++;
  }
  const lastAccident = (underway?.accident ? underway : nights.find((n) => n.accident))?.accident;
  const lastNight = underway?.accident ? underway : nights[0];

  if (!time) {
    if (off) return { suggested, who, dryNights, lastAccident, advice: 'Sleeping through — no night trip. If he starts waking and crying, a quick trip out is fine.' };
    return { suggested, who, dryNights, lastAccident, advice: 'Most pups his age can sleep through. If he wakes and cries in the night, take him out quietly and straight back to bed.' };
  }
  if (lastNight?.accident) {
    const at = lastNight.accident.at;
    const when = `Accident at ${formatTime(at)}${lastNight === underway ? '' : ' last night'}`;
    // Where it fell in the night decides the fix: an earlier alarm only helps an accident shortly before it.
    const bed = clockToMinutes(household.nightStart);
    const intoNight = (m: number) => (m - bed + 1440) % 1440;
    const accident = intoNight(minutesOfDay(at));
    const alarm = intoNight(clockToMinutes(time));
    const base = { time, suggested, who, dryNights, lastAccident };
    if (accident < alarm - 90)
      // Soon after lights out: the bedtime wee didn't empty him — the alarm isn't the problem.
      return { ...base, advice: `${when}, soon after bed — wait outside until the bedtime wee actually happens (and a poo if he’s due), and lift his water an hour before bed.` };
    if (accident > alarm) {
      // After the night trip: the stretch to morning is too long — move the trip later.
      const later = addMins(time, 30);
      if (clockToMinutes(later) < clockToMinutes(household.nightEnd) - 30)
        return { ...base, proposeTime: later, advice: `${when}, after his night trip — try the alarm 30 min later (${later}) to split the night more evenly.` };
      return { ...base, advice: `${when}, after his night trip — get up a little earlier for a few mornings.` };
    }
    const earlier = addMins(time, -30);
    return { ...base, proposeTime: earlier, advice: `${when} — try the alarm 30 min earlier (${earlier}) for a few nights.` };
  }
  if (dryNights >= 7 && weeks >= 12) return { time, suggested, who, dryNights, lastAccident, proposeOff: true, advice: `A week of dry nights — try skipping the night trip.` };
  if (dryNights >= 3) {
    const later = addMins(time, 30);
    // Don't push it into the morning.
    if (clockToMinutes(later) < clockToMinutes(household.nightEnd) - 30)
      return { time, suggested, who, dryNights, lastAccident, proposeTime: later, advice: `${dryNights} dry nights in a row — try setting it 30 min later (${later}).` };
    return { time, suggested, who, dryNights, lastAccident, proposeOff: weeks >= 12, advice: `${dryNights} dry nights — he’s nearly sleeping through${weeks >= 12 ? '; try skipping it' : ''}.` };
  }
  return {
    time,
    suggested,
    who,
    dryNights,
    lastAccident,
    advice: 'Carry him straight out to his spot, cue word, quiet praise, then straight back to bed — no play, no lights, no chat.',
  };
}

/** The alarm instant for the night that contains (or follows) `now`. */
export function nightTripAt(household: Pick<Household, 'nightStart' | 'nightEnd'>, time: string, now: Millis): Millis {
  // The trip is in the small hours after the evening that starts the night.
  const evening = inClockWindow(now, household.nightStart, household.nightEnd) && new Date(now).getHours() < 12 ? now - DAY : now;
  return atClock(clockToMinutes(time) < clockToMinutes(household.nightStart) ? dayStart(evening, 1) : evening, time);
}
